import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { BankData, BankId, BankService, BankTransfer } from '../../services/bank.service';

import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { FirestoreService } from '../../services/firestore/firestore.service';
import { Globals } from '../../globals';
import { IncomeId } from '../../services/income.service';
import { Outcome } from '../../services/outcome.service';
import { Utils } from '../../util';
import { AuthService } from '../../services/auth.service';

type Tran = { type: 'Ingreso' | 'Egreso' | 'Transferencia entrada' | 'Transferencia salida', date: string, amount: number, bankId?: string };

@Component({
  selector: 'app-display-bank',
  templateUrl: './display-bank.component.html',
  styleUrls: ['./display-bank.component.css']
})
export class DisplayBankComponent implements OnInit {

  bankForm: FormGroup;

  bankId: string;
  bank: BankId & { utility: 'income' | 'outcome' | 'both' };
  subsciptionBank: Subscription;

  constructor(
    private bankService: BankService,
    private bankData: BankData,
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private fsService: FirestoreService,
    private authService: AuthService,
  ) {
    this.bankId = this.activatedRoute.snapshot.paramMap.get('id');
  }

  ngOnInit() {
    this.createForm();

    this.subsciptionBank = this.bankService.getBanks(/* this.bankId */).subscribe( (banks: BankId[]) => {
      for(let bank of banks) if(bank.id == this.bankId){
        let {isIncome, isOutcome, currency: currencyKey, ...data} = bank;
        if(!isOutcome) data['utility'] = 'income';
        if(!isIncome) data['utility'] = 'outcome';
        this.bank = {
          utility: 'both',
          currency: this.bankData.currencies[currencyKey].fullName,
          isIncome,
          isOutcome,
          ...data
        };
        this.bankForm.patchValue(this.bank);
        this.bankForm.get('utility').setValue(this.bankData.valueToName[this.bank.utility]);
        this.loadTrans();
      }

      this.banks = banks.filter(bank => bank.id != this.bankId);
      this.bankInfoById = banks.reduce((acc, cur) => ({
        ...acc,
        [cur.id]: { bankName: cur.bankName, accountNumber: cur.accountNumber, CLABE: cur.CLABE },
      }), {});
    });
  }

  ngOnDestroy() {
    this.subsciptionBank.unsubscribe();
    if(this.incomeSub) this.incomeSub.unsubscribe();
    if(this.outcomeSub) this.outcomeSub.unsubscribe();
    if(this.originBankTransfersSub) this.originBankTransfersSub.unsubscribe();
    if(this.destinationBankTransfersSub) this.destinationBankTransfersSub.unsubscribe();
  }

  createForm() {
    this.bankForm = this.fb.group({
      bankName: ['', Validators.required],
      accountNumber: ['', Validators.required],
      CLABE: ['', [Validators.maxLength(18), Validators.minLength(18)]],
      utility: ['', Validators.required],
      currency: ['', Validators.required]
    });
  }

  /////////////// Transactions
  loadedIncomes: boolean = false;
  loadedOutcomes: boolean = false;

  trans: Tran[] = [];

  incomeSub: Subscription;
  outcomeSub: Subscription;
  originBankTransfersSub?: Subscription;
  destinationBankTransfersSub?: Subscription;

  loadTrans(){

    const colFilterFn = ref => {
      let newRef = ref.where('active', '==', true);
      if(this.bank.adjustedBalanceDatetime) newRef = newRef.where('createdAt', '>=', this.bank.adjustedBalanceDatetime);
      return newRef;
    };

    if(this.incomeSub) this.incomeSub.unsubscribe();
    if(this.outcomeSub) this.outcomeSub.unsubscribe();
    if(this.originBankTransfersSub) this.originBankTransfersSub.unsubscribe();
    if(this.destinationBankTransfersSub) this.destinationBankTransfersSub.unsubscribe();

    this.incomeSub = this.fsService.colWithIds$<IncomeId>(Globals.INCOME_COLLECTION, colFilterFn).subscribe(incomes => {
      this.trans = this.trans.filter(tran => tran.type != 'Ingreso');
      for(let income of incomes) if(income.bankRef.id == this.bankId) this.trans.push({type: 'Ingreso', date: income.date, amount: income.amount + (income.extraAmount || 0)});
      this.loadedIncomes = true;
    });

    this.outcomeSub = this.fsService.colWithIds$<Outcome>(Globals.OUTCOME_COLLECTION, colFilterFn).subscribe(outcomes => {
      this.trans = this.trans.filter(tran => tran.type != 'Egreso');
      for(let outcome of outcomes) if(outcome.bankRef.id == this.bankId) this.trans.push({type: 'Egreso', date: outcome.date, amount: -outcome.amount});
      this.loadedOutcomes = true;
    });

    this.originBankTransfersSub = this.bankService.getBankTransfers$({ originBankId: this.bank.id, datetimeAfter: this.bank.adjustedBalanceDatetime }).subscribe(bankTransfers => {
      this.trans = this.trans.filter(tran => tran.type != 'Transferencia salida');
      for(let bankTransfer of bankTransfers) this.trans.push({
        type: 'Transferencia salida',
        date: bankTransfer.datetime.replace('T', ', '),
        amount: -bankTransfer.amount,
      });
    });
    this.destinationBankTransfersSub = this.bankService.getBankTransfers$({ destinationBankId: this.bank.id, datetimeAfter: this.bank.adjustedBalanceDatetime }).subscribe(bankTransfers => {
      this.trans = this.trans.filter(tran => tran.type != 'Transferencia entrada');
      for(let bankTransfer of bankTransfers) this.trans.push({
        type: 'Transferencia entrada',
        date: bankTransfer.datetime.replace('T', ', '),
        amount: bankTransfer.amount,
      });
    });
  }

  get transTotal(){
    return this.trans.reduce((acc, cur) => acc + cur.amount, this.bank.adjustedBalance || 0);
  }

  async adjustBalance(){
    try{
      let newBalance: null | number = NaN;
      while(isNaN(newBalance)){
        let value = prompt(`Introduzca el saldo actual:`, this.transTotal.toFixed(2));
        if(value == null) return;
        newBalance = +value;
      }
  
      const datetime = `${Utils.today}T${Utils.time}`;
  
      await this.bankService.updateBank(this.bank.id, {
        adjustedBalance: newBalance,
        adjustedBalanceDatetime: datetime,
        adjustedBalanceSnapshots: [
          ...( this.bank.adjustedBalanceSnapshots || [] ),
          {
            userId: this.authService.currentUser.id,
            datetime,
            balance: newBalance,
            previousBalance: +this.transTotal.toFixed(2),
          },
        ],
      });
  
      this.loadTrans();
      //alert(`Se actualizó exitosamente el saldo de la cuenta.`);
    }catch(error){
      console.log('Error actualizando saldo.', error);
    }
  }

  banks: BankId[] = [];
  bankInfoById: { [id: string]: { bankName: string, accountNumber: string, CLABE: string } } = {};
  newBankTransfer: Pick<BankTransfer, 'destinationBankId' | 'amount'> = {
    destinationBankId: '',
    amount: 0,
  };
  async addBankTransfer(): Promise<void> {
    try{
      let { destinationBankId, amount } = this.newBankTransfer;
      console.log(destinationBankId, amount);
      if(!destinationBankId) return alert(`Seleccione una cuenta destino.`);
      if(!amount || isNaN(amount)) return alert(`Ingrese un monto válido.`);
      await this.bankService.addBankTransfer({
        originBankId: this.bank.id,
        destinationBankId,
        involvedBankIds: [ this.bank.id, destinationBankId ],
        datetime: `${Utils.today}T${Utils.time}`,
        amount,
      });
      alert(`Transferencia exitosa.`);
      this.newBankTransfer = { destinationBankId: '', amount: 0 };
    }catch(error){
      alert(`Error realizando la transferencia.`);
      console.log(error);
    }
  }

}
